import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bot } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Tiny inline badge that surfaces ONLY when the admin has flipped the
 * auto-credit feature to 24/7 mode. Gives the cashier a passive, at-a-glance
 * signal that the system is auto-crediting receipts in the background while
 * they're at the counter — so a wallet that's already credited before they
 * touch it isn't a surprise.
 *
 * Hidden when `always_on=false` (i.e. the after-hours-only default), so the
 * cashier UI stays clean during business hours when nothing's happening
 * automatically.
 *
 * Polls every 60s so the toggle state catches up if admin flips it remotely.
 */
const AutoCredit247Badge = () => {
    const [state, setState] = useState(null);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const { data } = await axios.get(`${API}/cashier/auto-credit-status`, { withCredentials: true });
                if (alive) setState(data);
            } catch { /* silent — badge just stays hidden */ }
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    if (!state || !state.enabled || !state.always_on) return null;

    return (
        <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#FFD700]/15 border border-[#FFD700]/50 text-[#FFD700]"
            title="The system is auto-crediting payment receipts in the background — a player's wallet may credit before you touch it."
            data-testid="cashier-auto-credit-247-badge"
        >
            <Bot className="w-3 h-3" />
            <span>Auto-credit · 24/7</span>
        </div>
    );
};

export default AutoCredit247Badge;
